import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Paper, Stack, Divider, Chip, Button,
  Modal, Box, TextField, FormControlLabel, Checkbox, Alert, MenuItem, IconButton,
  Select, FormControl, InputLabel
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/EditOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../../utils/serviceName';
import { formatMoney } from '../../utils/money';
import { buildMessage, MESSAGE_TEMPLATES, MESSAGE_LANGS } from '../../utils/clientMessages';
import dayjs from 'dayjs';
import { api } from '../../api';
import { getCurrentUser } from '../../services/auth';

// Парсинг грошової суми: приймає і крапку, і кому як десятковий роздільник
// (на деяких iOS-локалях клавіатура дає кому). Повертає число (NaN, якщо не число).
const parseMoney = (v) => Number(String(v ?? '').trim().replace(',', '.'));

// Ефективна сума до сплати за бронювання:
// кастомна ціна брони (якщо майстер її задав при створенні/редагуванні) або сума
// послуг — мінус знижка промокоду. Саме її підставляємо як дефолт на чекауті.
const effectiveAmount = (bk, serviceObjs) => {
  const base = bk?.price != null && bk.price !== ''
    ? Number(bk.price)
    : (serviceObjs || []).reduce((acc, s) => acc + Number(s.price || 0), 0);
  return Math.max(0, base - Number(bk?.discountAmount || 0));
};

// Доступні причини для майстра
const cancellationReasons = [
  { value: 'master', label: 'bookingDetails.reasonMaster' },
  { value: 'client', label: 'bookingDetails.reasonClient' },
  { value: 'no_show', label: 'bookingDetails.reasonNoShow' },
  { value: 'other', label: 'bookingDetails.reasonOther' },
];

function getAvailableSlots({ date, duration, bookings, currentBookingId }) {
  const slots = [];
  const day = dayjs(date).startOf('day');
  for (let h = 7; h < 21; h++) {
    for (let m = 0; m < 60; m += 15) {
      const slotStart = day.hour(h).minute(m).second(0).millisecond(0);
      const slotEnd = slotStart.add(duration, 'minute');
      const conflict = bookings.some(b => {
        if (b.id === currentBookingId) return false;
        const bStart = dayjs(`${b.date.slice(0, 10)}T${b.time}`);
        // Слід у календарі = час бронювання (durationMinutes / blockDuration)
        const bDuration = b.durationMinutes ?? (Array.isArray(b.services)
          ? b.services.reduce((acc, s) => acc + Number(s.blockDuration ?? s.duration ?? 0), 0)
          : 0);
        const bEnd = bStart.add(bDuration, 'minute');
        return slotStart.isBefore(bEnd) && bStart.isBefore(slotEnd);
      });
      if (!conflict) {
        slots.push(slotStart);
      }
    }
  }
  return slots;
}

export default function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [record, setRecord] = useState(null);
  const [userStatus, setUserStatus] = useState('white');
  const [openHistory, setOpenHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // --- EDITING ---
  const [allServices, setAllServices] = useState([]);
  const [editing, setEditing] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [editedDate, setEditedDate] = useState(dayjs());
  const [editedTime, setEditedTime] = useState('');
  const [editedPrice, setEditedPrice] = useState(''); // порожнє = сума послуг
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pastConfirmOpen, setPastConfirmOpen] = useState(false); // підтвердження переносу в минуле

  // --- Checkout ---
  const [checkedServices, setCheckedServices] = useState([]);
  const [actualAmount, setActualAmount] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [servicePickerOpen, setServicePickerOpen] = useState(false);

  const [bookingsThisDay, setBookingsThisDay] = useState([]);
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null); // наявний чекаут (для виправлення)
  const [checkoutSavedMsg, setCheckoutSavedMsg] = useState('');

  // --- Зміна ціни запису (доступна будь-коли: майбутні/минулі/завершені) ---
  const [priceEditOpen, setPriceEditOpen] = useState(false);
  const [newPrice, setNewPrice] = useState('');
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceSavedMsg, setPriceSavedMsg] = useState('');
  const [priceError, setPriceError] = useState('');

  // --- Оплата ---
  const [payLinkOpen, setPayLinkOpen] = useState(false);
  const [payCheckedServices, setPayCheckedServices] = useState([]);
  const [payAmount, setPayAmount] = useState('');
  const [payTip, setPayTip] = useState('');
  const [payUrl, setPayUrl] = useState('');
  const [payCopy, setPayCopy] = useState('');
  const [manualAmount, setManualAmount] = useState(false);
  const [currentMaster, setCurrentMaster] = useState(null);

  // --- Клієнтські деталі ---
  const [clientDetailsOpen, setClientDetailsOpen] = useState(false);
  const [clientBookings, setClientBookings] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);

  // --- Редагування даних клієнта (лише для клієнта без акаунта) ---
  const [clientEditing, setClientEditing] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', phone: '', email: '' });
  const [clientSaving, setClientSaving] = useState(false);
  const [clientEditError, setClientEditError] = useState('');
  const [clientSavedMsg, setClientSavedMsg] = useState('');

  // --- Повідомити клієнта (SMS/email, шаблони 3 мовами) ---
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyLang, setNotifyLang] = useState('pl');
  const [notifyTemplate, setNotifyTemplate] = useState('reminder');
  const [notifySms, setNotifySms] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyCode, setNotifyCode] = useState('');
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null); // { ok, msg } | null
  const [notifyError, setNotifyError] = useState('');

  // --- Модалка вибору причини скасування ---
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const booking = await api.getBookingById(id);
        let client = null;
        if (booking.userId) {
          const user = await api.getUserById(booking.userId);
          client = user;
        }
        const services = await api.getServices(booking.masterId);

        let serviceObjs = [];
        if (Array.isArray(booking.services) && booking.services.length > 0) {
          if (typeof booking.services[0] === 'object') {
            serviceObjs = booking.services;
          } else {
            serviceObjs = services.filter(s => booking.services.includes(s.id));
          }
        }
        const user = await getCurrentUser();
        if (!user?.id) throw new Error('User not found');
        const users = await api.getUsers();
        const me = users.find(u => u.id === user.id);
        if (!me) throw new Error('User not found');
        setCurrentMaster(me);

        setRecord({
          ...booking,
          clientName: client?.name || t('bookingDetails.unknown'),
          clientEmail: client?.email || '',
          clientPhone: client?.phone || '',
          _user: client,
          serviceDetails: serviceObjs
        });
        setUserStatus(client?.status || 'white');
        setAllServices(services);

        setSelectedServices(serviceObjs.map(s => s.id));
        setEditedDate(dayjs(booking.date.slice(0, 10)));
        setEditedTime(dayjs(`${booking.date.slice(0, 10)}T${booking.time}`).format('HH:mm'));
        setEditedPrice(booking.price != null ? String(Number(booking.price)) : '');

        setCheckedServices(serviceObjs.map(s => String(s.id)));
        // Дефолт чекауту = ефективна ціна брони (кастомна/зі знижкою), а не сума послуг
        setActualAmount(String(effectiveAmount(booking, serviceObjs)));
        setTipAmount('');

        setPayCheckedServices(serviceObjs.map(s => String(s.id)));
        setPayAmount(serviceObjs.reduce((acc, s) => acc + (s.price || 0), 0).toString());
        setPayTip('');
        setPayUrl('');
        setManualAmount(false);

        setHistory(booking.history || []);

        // всі букінги цього майстра на цю дату
        const allBookings = await api.getBookings();
        const filtered = allBookings.filter(
          b => b.masterId === booking.masterId && b.date.slice(0, 10) === booking.date.slice(0, 10)
        );
        setBookingsThisDay(filtered);

        // --- Перевірка на наявність чекауту ---
        try {
          const checkouts = await api.getCheckoutsByBookingId(booking.id);
          if (checkouts && Object.keys(checkouts).length) {
            setIsCheckedOut(true);
            setCheckoutData(checkouts); // { actualAmount, tip, performedServices }
          } else {
            setIsCheckedOut(false);
          }
        } catch (e) {
          setIsCheckedOut(false);
        }
      } catch (e) {
        setRecord(null);
      }
    }
    fetchData();
  }, [id]);

  useEffect(() => {
    if (!record || !editedDate) return;
    async function fetchBookingsForDay() {
      const allBookings = await api.getBookings();
      const filtered = allBookings.filter(
        b =>
          b.masterId === record.masterId &&
          b.date.slice(0, 10) === editedDate.format('YYYY-MM-DD')
      );
      setBookingsThisDay(filtered);
    }
    fetchBookingsForDay();
  }, [editedDate, record?.masterId]);

  // Для drop-down доступних слотів:
  const selectedServiceObjs = useMemo(
    () => allServices.filter(s => selectedServices.includes(s.id)),
    [allServices, selectedServices]
  );
  const totalDuration = selectedServiceObjs.reduce((acc, s) => acc + (s.duration || 0), 0); // час роботи (показ)
  // Слід у календарі = сумарний час бронювання (blockDuration) — для розрахунку слотів
  const totalBlockDuration = selectedServiceObjs.reduce((acc, s) => acc + Number(s.blockDuration ?? s.duration ?? 0), 0);

  // Ціна: індивідуальна (record.price) або стандартна сума послуг
  const servicesSum = (record?.serviceDetails || []).reduce((acc, s) => acc + Number(s.price || 0), 0);
  const effectivePrice = record?.price != null ? Number(record.price) : servicesSum;
  // Знижка по промокоду (знімок на записі) і фінальна ціна
  const discountAmount = Number(record?.discountAmount || 0);
  const finalPrice = Math.max(0, Math.round((effectivePrice - discountAmount) * 100) / 100);
  // Сума обраних у модалці редагування послуг — для плейсхолдера поля ціни
  const selectedServicesSum = selectedServiceObjs.reduce((acc, s) => acc + Number(s.price || 0), 0);

  const availableSlots = useMemo(() => {
    if (!record || totalBlockDuration === 0 || !editedDate) return [];
    return getAvailableSlots({
      date: editedDate.format('YYYY-MM-DD'),
      duration: totalBlockDuration,
      bookings: bookingsThisDay,
      currentBookingId: record.id
    });
  }, [record, totalBlockDuration, bookingsThisDay, editedDate]);

  // Відкрити модалку редагування зі свіжими значеннями поточного запису
  const openEdit = () => {
    setEditError('');
    setSelectedServices(record.serviceDetails.map(s => s.id));
    setEditedDate(dayjs(record.date.slice(0, 10)));
    setEditedTime(dayjs(`${record.date.slice(0, 10)}T${record.time}`).format('HH:mm'));
    setEditedPrice(record.price != null ? String(Number(record.price)) : '');
    setEditing(true);
  };

  const handleSaveChanges = async (allowPast = false) => {
    setEditError('');

    if (!editedTime || selectedServices.length === 0) {
      setEditError(t('bookingDetails.selectTimeAndService'));
      return;
    }

    const slot = availableSlots.find(s => s.format('HH:mm') === editedTime);
    if (!slot) {
      setEditError(t('bookingDetails.timeUnavailable'));
      return;
    }

    // Перенос у минуле — підтвердження ЛИШЕ якщо майстер реально змінив дату/час
    // на минулий (а не просто зберіг ту саму, вже-минулу дату, редагуючи ціну/послуги).
    const origDateTime = dayjs(`${String(record.date).slice(0, 10)}T${String(record.time).slice(0, 5)}`);
    const newDateTime = dayjs(`${slot.format('YYYY-MM-DD')}T${slot.format('HH:mm')}`);
    const dateTimeChanged = !newDateTime.isSame(origDateTime);
    const isPast = newDateTime.isBefore(dayjs());
    if (isPast && dateTimeChanged && !allowPast) {
      setPastConfirmOpen(true);
      return;
    }

    setSaving(true);
    try {
      await api.updateBooking(record.id, {
        time: slot.format('HH:mm:ss'),
        date: slot.format('YYYY-MM-DD'),
        services: selectedServices,
        price: editedPrice === '' ? null : Number(editedPrice),
        allowPast: isPast || undefined,
      });
      setPastConfirmOpen(false);
      setEditing(false);
      // Перезавантажити букінг
      const updated = await api.getBookingById(record.id);
      let serviceObjs = [];
      if (typeof updated.services[0] === 'object') {
        serviceObjs = updated.services;
      } else {
        serviceObjs = allServices.filter(s => updated.services.includes(s.id));
      }
      setRecord({
        ...updated,
        clientName: record.clientName,
        clientEmail: record.clientEmail,
        clientPhone: record.clientPhone,
        _user: record._user,
        serviceDetails: serviceObjs,
      });
      setSelectedServices(serviceObjs.map(s => s.id));
      setEditedDate(dayjs(updated.date.slice(0, 10)));
      setEditedTime(dayjs(`${updated.date.slice(0, 10)}T${updated.time}`).format('HH:mm'));
      setEditedPrice(updated.price != null ? String(Number(updated.price)) : '');
      setCheckedServices(serviceObjs.map(s => String(s.id)));
      setActualAmount(String(effectiveAmount(updated, serviceObjs)));
    } catch (e) {
      setEditError(e.message || t('bookingDetails.saveFailed'));
    }
    setSaving(false);
  };

  // Зміна ціни запису — окрема дія, шле лише { price } (тому гард дати не спрацьовує)
  const openPriceEdit = () => {
    setPriceError('');
    setPriceSavedMsg('');
    setNewPrice(record?.price != null ? String(Number(record.price)) : '');
    setPriceEditOpen(true);
  };

  const handleSavePrice = async () => {
    setPriceError('');
    if (newPrice !== '' && (!Number.isFinite(parseMoney(newPrice)) || parseMoney(newPrice) < 0)) {
      setPriceError(t('bookingDetails.priceInvalid'));
      return;
    }
    setPriceSaving(true);
    try {
      const priceVal = newPrice === '' ? null : parseMoney(newPrice);
      await api.updateBooking(record.id, { price: priceVal });
      setRecord(prev => ({ ...prev, price: priceVal }));
      setPriceEditOpen(false);
      setPriceSavedMsg(t('bookingDetails.priceSaved'));
      setTimeout(() => setPriceSavedMsg(''), 2500);
    } catch (e) {
      setPriceError(e.message || t('bookingDetails.saveFailed'));
    }
    setPriceSaving(false);
  };

  // Відкрити чекаут: для вже завершеного — підставити реальні значення (виправлення)
  const openCheckout = () => {
    setCheckoutSavedMsg('');
    if (isCheckedOut && checkoutData) {
      setActualAmount(String(Number(checkoutData.actualAmount ?? 0)));
      setTipAmount(checkoutData.tip != null ? String(Number(checkoutData.tip)) : '');
      if (Array.isArray(checkoutData.performedServices)) {
        setCheckedServices(checkoutData.performedServices.map(String));
      }
    } else {
      // Свіжий чекаут: дефолт = ефективна ціна поточного запису (кастомна/зі знижкою),
      // перераховуємо на момент відкриття — щоб врахувати можливу зміну ціни після завантаження
      setActualAmount(String(effectiveAmount(record, record.serviceDetails)));
      setCheckedServices((record.serviceDetails || []).map(s => String(s.id)));
      setTipAmount('');
    }
    setCheckoutOpen(true);
  };

  const handleCheckout = async () => {
    if (checkedServices.length === 0) {
      alert(t('bookingDetails.selectAtLeastOneService'));
      return;
    }
    try {
      await api.checkoutBooking(record.id, {
        performedServices: checkedServices.map(Number),
        actualAmount: parseMoney(actualAmount),
        tip: parseMoney(tipAmount) || 0,
      });
      setCheckoutOpen(false);
      setIsCheckedOut(true);
      // Оновлюємо локальний знімок чекауту — щоб повторне виправлення бачило свіжі значення
      setCheckoutData({
        actualAmount: parseMoney(actualAmount),
        tip: parseMoney(tipAmount) || 0,
        performedServices: checkedServices.map(Number),
      });
      setCheckoutSavedMsg(t('bookingDetails.checkoutSaved'));
      setTimeout(() => setCheckoutSavedMsg(''), 2500);
    } catch (e) {
      alert(e.message || t('bookingDetails.checkoutError'));
    }
  };

  useEffect(() => {
    if (!manualAmount) {
      const selectedObjs = allServices.filter(s => payCheckedServices.includes(String(s.id)));
      const baseSum = selectedObjs.reduce((acc, s) => acc + Number(s.price || 0), 0);
      setPayAmount(baseSum.toString());
    }
  }, [payCheckedServices, allServices, manualAmount]);

  const handleGeneratePaymentLink = async () => {
    setPayUrl('');
    setPayCopy('');
    try {
      const res = await api.createPaymentLink({
        description: record.id,
        bookingId: record.id,
        amount: (parseMoney(payAmount) + parseMoney(payTip)) || 0,
      });
      setPayUrl(res.url);
    } catch (e) {
      setPayUrl(t('bookingDetails.linkCreationError'));
    }
  };

  const startClientEdit = () => {
    setClientEditError('');
    setClientForm({
      name: record._user?.name || '',
      phone: record._user?.phone || '',
      email: record._user?.email || '',
    });
    setClientEditing(true);
  };

  const handleSaveClient = async () => {
    setClientEditError('');
    setClientSaving(true);
    try {
      const updated = await api.updateUser(record._user.id, {
        name: clientForm.name,
        phone: clientForm.phone,
        email: clientForm.email || null,
      });
      // Оновити відображувані дані клієнта в записі
      setRecord(prev => ({
        ...prev,
        clientName: updated.name,
        clientPhone: updated.phone || '',
        clientEmail: updated.email || '',
        _user: { ...prev._user, name: updated.name, phone: updated.phone, email: updated.email },
      }));
      setClientEditing(false);
      setClientSavedMsg(t('bookingDetails.clientSaved'));
      setTimeout(() => setClientSavedMsg(''), 2000);
    } catch (e) {
      setClientEditError(e.message || t('bookingDetails.saveClientFailed'));
    }
    setClientSaving(false);
  };

  // Перебудувати текст повідомлення з шаблону/мови/коду + даних запису
  const rebuildMsg = (key, lang, code) => {
    const { subject, body } = buildMessage(key, lang, {
      name: record?.clientName && record.clientName !== t('bookingDetails.unknown') ? record.clientName : '',
      date: record?.date ? String(record.date).slice(0, 10) : '',
      time: record?.time ? String(record.time).slice(0, 5) : '',
      code: code || '',
    });
    setNotifySubject(subject);
    setNotifyBody(body);
  };

  const openNotify = () => {
    const lang = MESSAGE_LANGS.includes(i18n.language) ? i18n.language : 'pl';
    const code = (() => { try { return localStorage.getItem('chc-door-code') || ''; } catch { return ''; } })();
    setNotifyLang(lang);
    setNotifyTemplate('reminder');
    setNotifySms(!!record.clientPhone);
    setNotifyEmail(false);
    setNotifyCode(code);
    setNotifyResult(null);
    setNotifyError('');
    rebuildMsg('reminder', lang, code);
    setNotifyOpen(true);
  };

  const changeTemplate = (key) => { setNotifyTemplate(key); rebuildMsg(key, notifyLang, notifyCode); };
  const changeLang = (lang) => { setNotifyLang(lang); rebuildMsg(notifyTemplate, lang, notifyCode); };
  const changeCode = (code) => {
    setNotifyCode(code);
    try { localStorage.setItem('chc-door-code', code); } catch { /* ignore */ }
    rebuildMsg(notifyTemplate, notifyLang, code);
  };

  const handleSendNotify = async () => {
    setNotifyError('');
    setNotifyResult(null);
    const channel = notifySms && notifyEmail ? 'both' : notifySms ? 'sms' : notifyEmail ? 'email' : null;
    if (!channel) { setNotifyError(t('notify.pickChannel')); return; }
    if (!notifyBody.trim()) { setNotifyError(t('notify.emptyBody')); return; }
    setNotifySending(true);
    try {
      const res = await api.notifyClient(record.id, { channel, subject: notifySubject, body: notifyBody });
      const parts = [];
      if (res.sent?.sms) parts.push('SMS');
      if (res.sent?.email) parts.push('Email');
      setNotifyResult({ ok: true, msg: t('notify.sentVia', { channels: parts.join(' + ') }) });
    } catch (e) {
      setNotifyError(e.message || t('notify.failed'));
    }
    setNotifySending(false);
  };

  const handleOpenClientDetails = async () => {
    setClientDetailsOpen(true);
    setClientEditing(false);
    setClientEditError('');
    setClientLoading(true);
    try {
      // Історія клієнта: активні + архівні (скасовані/неявки) у цього майстра
      const rows = await api.getClientBookings(record.userId);
      const now = dayjs();
      const mapped = rows.map(b => {
        const start = dayjs(`${b.date}T${b.time}`);
        let status;
        if (b.src === 'archive') status = b.reason === 'no_show' ? 'no_show' : 'cancelled';
        else status = start.isAfter(now) ? 'upcoming' : 'completed';
        return { ...b, status };
      });
      setClientBookings(mapped);
    } catch (e) {
      setClientBookings([]);
    }
    setClientLoading(false);
  };

  // Локалізовані назви послуг за масивом id (для історії клієнта)
  const serviceNamesByIds = (ids) =>
    (Array.isArray(ids) ? ids : [])
      .map(id => allServices.find(s => s.id === id))
      .filter(Boolean)
      .map(s => pickServiceName(s, i18n.language))
      .join(', ');

  // Підсумки по клієнту для шапки модалки
  const clientStats = useMemo(() => {
    const acc = { total: clientBookings.length, completed: 0, upcoming: 0, cancelled: 0, no_show: 0 };
    for (const b of clientBookings) if (acc[b.status] !== undefined) acc[b.status] += 1;
    return acc;
  }, [clientBookings]);

  // --- No Show ---
  const showNoShowButton = useMemo(() => {
    if (!record) return false;
    const now = dayjs();
    const bookingDateTime = dayjs(`${record.date.slice(0, 10)}T${record.time}`);
    return (
      bookingDateTime.isBefore(now) &&
      !isCheckedOut &&
      !record.isCancelled
    );
  }, [record, isCheckedOut]);

  const handleNoShow = async () => {
    if (window.confirm(t('bookingDetails.confirmNoShow'))) {
      await api.cancelBooking(record.id, { reason: 'no_show' });
      navigate('/master');
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelReason) return;
    await api.cancelBooking(record.id, { reason: cancelReason });
    setCancelModalOpen(false);
    navigate('/master');
  };

  if (!record) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ color: '#1a1a16' }}>
            {t('bookingDetails.title')}
          </Typography>
          <Button variant="outlined" color="inherit" onClick={() => navigate('/master/dashboard')}>
            ← {t('common.back')}
          </Button>
        </Stack>

        <Paper elevation={3} sx={{ p: 3, bgcolor: 'linear-gradient(135deg, #efeae2, #efeae2)', color: '#1a1a16', border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2 }}>
          <Stack spacing={2}>
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.12)' }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography><strong>{t('bookingDetails.client')}:</strong> {record.clientName}</Typography>
              {record.userId && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleOpenClientDetails}
                  sx={{ ml: 1, fontSize: 12, textTransform: 'none' }}
                >
                  {t('bookingDetails.clientDetails')}
                </Button>
              )}
            </Stack>
            {record.clientEmail && <Typography><strong>{t('common.email')}:</strong> {record.clientEmail}</Typography>}
            {record.clientPhone && <Typography><strong>{t('common.phone')}:</strong> {record.clientPhone}</Typography>}

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight="bold">{t('bookingDetails.clientStatus')}:</Typography>
              <Chip
                label={userStatus}
                size="small"
                sx={{
                  bgcolor:
                    userStatus === 'white' ? '#4caf50' :
                    userStatus === 'gray' ? '#ff9800' :
                    userStatus === 'black' ? '#f44336' :
                    '#757575',
                  color: '#1a1a16',
                  textTransform: 'capitalize'
                }}
                onClick={() => {
                  const next = userStatus === 'white' ? 'gray' : userStatus === 'gray' ? 'black' : 'white';
                  api.updateUserStatus(record._user.id, next).then(() => setUserStatus(next));
                }}
              />
            </Stack>
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.12)' }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}
              alignItems={{ xs: 'flex-start', sm: 'center' }} useFlexGap flexWrap="wrap">
              <Typography><strong>{t('bookingDetails.date')}:</strong> {record.date.slice(0, 10)}</Typography>
              <Typography><strong>{t('bookingDetails.time')}:</strong> {record.time.slice(0, 5)}</Typography>
              <Typography>
                <strong>{t('bookingDetails.terms')}:</strong>{' '}
                {record.termsAccepted ? (
                  <span style={{ color: '#5e6a4e' }}>
                    {t('bookingDetails.accepted')}{record.termsAcceptedAt ? ` ${dayjs(record.termsAcceptedAt).format('DD.MM.YYYY HH:mm')}` : ''}
                    {record.termsVersion ? ` (${record.termsVersion})` : ''}
                  </span>
                ) : (
                  <span style={{ color: '#977536' }}>{t('bookingDetails.awaitingClientConfirmation')}</span>
                )}
              </Typography>
              {!record.termsAccepted && record.confirmHash && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {t('bookingDetails.clientLink', { url: `${window.location.origin}/confirm/${record.confirmHash}` })}
                </Typography>
              )}
              <Typography>
                <strong>{t('bookingDetails.duration')}:</strong> {`${record.serviceDetails.reduce((acc, s) => acc + (s.duration || 0), 0)} ${t('common.min')}`}
              </Typography>
            </Stack>
            {isCheckedOut && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {t('bookingDetails.checkoutDoneNoEdit')}
              </Alert>
            )}
            {checkoutSavedMsg && (
              <Alert severity="success" sx={{ mt: 1 }}>{checkoutSavedMsg}</Alert>
            )}
            {priceSavedMsg && (
              <Alert severity="success" sx={{ mt: 1 }}>{priceSavedMsg}</Alert>
            )}

            <Divider sx={{ borderColor: 'rgba(28,29,23,0.12)' }} />
            <Typography><strong>{t('bookingDetails.services')}:</strong></Typography>
            <Stack spacing={1}>
              {record.serviceDetails.map((s, idx) => (
                <Paper key={idx} elevation={1} sx={{ p: 1.5, bgcolor: '#faf7f2', border: '1px solid rgba(28,29,23,0.18)' }}>
                  <Typography><strong>{t('bookingDetails.serviceName')}:</strong> {pickServiceName(s, i18n.language)}</Typography>
                  <Typography><strong>{t('bookingDetails.duration')}:</strong> {s.duration} {t('common.min')}</Typography>
                  <Typography><strong>{t('bookingDetails.price')}:</strong> {formatMoney(s.price)}</Typography>
                </Paper>
              ))}
            </Stack>

            {/* Підсумкова ціна запису: база (індивідуальна/сума послуг), знижка, до сплати */}
            <Stack spacing={0.25}>
              <Typography sx={{ fontSize: '1.05rem' }}>
                <strong>{t('bookingDetails.price')}:</strong>{' '}
                <span style={{ textDecoration: discountAmount > 0 ? 'line-through' : 'none' }}>
                  {formatMoney(effectivePrice)}
                </span>
                {record.price != null && (
                  <span style={{ color: '#977536' }}> ({t('bookingDetails.customPrice')})</span>
                )}
              </Typography>
              {discountAmount > 0 && (
                <>
                  <Typography variant="body2" sx={{ color: '#5e6a4e' }}>
                    {t('bookingDetails.promoCode')}: <b>{record.promoCode || '—'}</b>
                    {'  ·  '}{t('bookingDetails.discount')}: −{formatMoney(discountAmount)}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>
                    {t('bookingDetails.finalPrice')}: {formatMoney(finalPrice)}
                  </Typography>
                </>
              )}
            </Stack>

            <Divider sx={{ borderColor: 'rgba(28,29,23,0.12)' }} />
            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={openEdit}
                disabled={isCheckedOut}
                sx={{
                  bgcolor: '#c9a24a', color: '#1a1a16', fontWeight: 600,
                  textTransform: 'none', boxShadow: 'none',
                  '&:hover': { bgcolor: '#977536' }
                }}
              >
                {t('bookingDetails.editBooking')}
              </Button>
              {/* Зміна ціни — доступна завжди (майбутні/минулі/завершені) */}
              <Button
                variant="outlined"
                onClick={openPriceEdit}
                sx={{ textTransform: 'none', borderColor: '#c9a24a', color: '#977536', fontWeight: 600 }}
              >
                {t('bookingDetails.changePrice')}
              </Button>
              <Button
                variant="contained"
                onClick={openNotify}
                sx={{
                  bgcolor: '#5e6a4e', color: '#fff', fontWeight: 600,
                  textTransform: 'none', boxShadow: 'none',
                  '&:hover': { bgcolor: '#4a5540' }
                }}
              >
                ✉️ {t('notify.button')}
              </Button>
              <Button variant="outlined" onClick={() => setOpenHistory(true)}>
                {t('bookingDetails.bookingHistory')}
              </Button>
              {/* Чекаут доступний будь-коли (без обмеження по даті) */}
              <Button variant="outlined" color="primary" onClick={openCheckout}>
                {isCheckedOut ? t('bookingDetails.editCheckout') : 'Checkout'}
              </Button>
              { currentMaster.stripeAccountSetupCompleted && (
                <Button
                  variant="outlined"
                  onClick={() => setPayLinkOpen(true)}
                >
                  {t('bookingDetails.generatePaymentLink')}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={() => setCancelModalOpen(true)}
                disabled={isCheckedOut}
              >
                {t('bookingDetails.cancelBooking')}
              </Button>
              {showNoShowButton && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleNoShow}
                >
                  No Show
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {/* Edit Modal */}
        <Modal open={editing && !isCheckedOut} onClose={() => setEditing(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
            boxShadow: 24, p: { xs: 3, sm: 4 }, width: '90%', maxWidth: 460,
            maxHeight: '90vh', overflowY: 'auto'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{t('bookingDetails.editTitle')}</Typography>
              <IconButton onClick={() => setEditing(false)} size="small" sx={{ color: '#1a1a16' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.18)', mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('bookingDetails.selectDateTime')}</Typography>
            <Stack spacing={2} sx={{ mb: 3 }}>
              {/* disablePast прибрано: майстер може зафіксувати минулий час (з підтвердженням) */}
              <DatePicker
                label={t('bookingDetails.date')}
                value={editedDate}
                onChange={d => setEditedDate(d)}
                format="YYYY-MM-DD"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                select
                fullWidth
                label={t('bookingDetails.time')}
                value={editedTime}
                onChange={e => setEditedTime(e.target.value)}
              >
                {availableSlots.length === 0 && (
                  <MenuItem disabled>{t('bookingDetails.noSlots')}</MenuItem>
                )}
                {availableSlots.map(s =>
                  <MenuItem key={s.format('HH:mm')} value={s.format('HH:mm')}>
                    {s.format('HH:mm')}
                  </MenuItem>
                )}
              </TextField>
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('bookingDetails.selectServices')}:</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
              {allServices.map((s) => {
                const on = selectedServices.includes(s.id);
                return (
                  <Chip
                    key={s.id}
                    label={`${pickServiceName(s, i18n.language)} · ${s.duration} ${t('common.min')}`}
                    onClick={() => setSelectedServices(prev =>
                      prev.includes(s.id) ? prev.filter(i => i !== s.id) : [...prev, s.id]
                    )}
                    variant={on ? 'filled' : 'outlined'}
                    sx={{
                      bgcolor: on ? '#c9a24a' : 'transparent',
                      color: '#1a1a16',
                      borderColor: 'rgba(28,29,23,0.3)',
                      fontWeight: on ? 600 : 400,
                      '&:hover': { bgcolor: on ? '#977536' : 'rgba(201,162,74,0.15)' }
                    }}
                  />
                );
              })}
            </Stack>
            <Typography variant="body2" sx={{ color: '#57564d', mb: 2 }}>
              {t('bookingDetails.duration')}: <strong>{totalDuration} {t('common.min')}</strong>
            </Typography>

            <TextField
              fullWidth
              label={t('bookingDetails.priceField')}
              value={editedPrice}
              onChange={e => setEditedPrice(e.target.value)}
              placeholder={String(selectedServicesSum)}
              helperText={t('bookingDetails.priceHint')}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ mb: 2 }}
            />

            {editError && <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>}

            <Stack direction="row" spacing={2}>
              <Button variant="outlined" fullWidth onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleSaveChanges()}
                disabled={saving}
                sx={{
                  bgcolor: '#c9a24a', color: '#1a1a16', fontWeight: 600,
                  textTransform: 'none', boxShadow: 'none',
                  '&:hover': { bgcolor: '#977536' }
                }}
              >
                {saving ? t('common.saving') : t('bookingDetails.saveChanges')}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* Checkout Modal */}
        <Modal open={checkoutOpen} onClose={() => setCheckoutOpen(false)}>
          <Box sx={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2',
            color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: '90%', maxWidth: 400
          }}>
            <Typography variant="h6" gutterBottom>
              {isCheckedOut ? t('bookingDetails.editCheckoutTitle') : t('bookingDetails.checkoutTitle')}
            </Typography>
            {isCheckedOut && (
              <Typography variant="body2" sx={{ color: '#977536', mb: 1 }}>
                {t('bookingDetails.editCheckoutHint')}
              </Typography>
            )}
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.18)', mb: 2 }} />

            <Typography variant="body2" sx={{ mb: 1 }}>{t('bookingDetails.performedServices')}:</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              {allServices
                .filter(s => checkedServices.includes(String(s.id)))
                .map(s => (
                  <Chip
                    key={s.id}
                    label={pickServiceName(s, i18n.language)}
                    size="small"
                    variant="outlined"
                    sx={{ bgcolor: '#efeae2', color: '#1a1a16', borderColor: '#777' }}
                  />
                ))}
              <Button
                size="small"
                onClick={() => setServicePickerOpen(true)}
                sx={{ color: '#c9a24a', fontWeight: 'bold' }}
              >
                {t('bookingDetails.change')}
              </Button>
            </Stack>

            <TextField
              label={t('bookingDetails.actualAmount')}
              fullWidth
              value={actualAmount}
              onChange={(e) => setActualAmount(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              label={t('bookingDetails.tip')}
              fullWidth
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ inputMode: 'decimal' }}
            />

            <Stack direction="row" spacing={2}>
              <Button variant="outlined" fullWidth onClick={() => setCheckoutOpen(false)}>{t('common.cancel')}</Button>
              <Button variant="contained" fullWidth onClick={handleCheckout}>
                {isCheckedOut ? t('common.save') : t('common.confirm')}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* Service Picker Modal */}
        <Modal open={servicePickerOpen} onClose={() => setServicePickerOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%', maxWidth: 400, maxHeight: '85vh', overflowY: 'auto',
            bgcolor: '#faf7f2', color: '#1a1a16',
            borderRadius: 2, border: '1px solid rgba(28,29,23,0.18)',
            p: 3, overflowY: 'auto'
          }}>
            <Typography variant="h6" gutterBottom>{t('bookingDetails.selectServices')}</Typography>
            <Stack spacing={1} mb={2}>
              {allServices.map((s, idx) => {
                const selected = checkedServices.includes(String(s.id));
                return (
                  <Paper
                    key={idx}
                    sx={{
                      p: 1,
                      bgcolor: '#efeae2',
                      border: '1px solid rgba(28,29,23,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Typography variant="body2">
                      {pickServiceName(s, i18n.language)} — {s.duration} {t('common.min')} / {formatMoney(s.price)}
                    </Typography>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        setCheckedServices(prev =>
                          selected
                            ? prev.filter(i => i !== String(s.id))
                            : [...prev, String(s.id)]
                        )
                      }
                    />
                  </Paper>
                );
              })}
            </Stack>
            <Button variant="contained" fullWidth onClick={() => setServicePickerOpen(false)}>
              {t('bookingDetails.apply')}
            </Button>
          </Box>
        </Modal>

        {/* History Modal */}
        <Modal open={openHistory} onClose={() => setOpenHistory(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
            boxShadow: 24, p: 3, width: '90%', maxWidth: 400, maxHeight: '85vh', overflowY: 'auto',
            overflowY: 'auto'
          }}>
            <Typography variant="h6" gutterBottom>{t('bookingDetails.bookingHistory')}</Typography>
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.18)', mb: 2 }} />
            {history.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#57564d' }}>{t('bookingDetails.historyEmpty')}</Typography>
            ) : (
              <Stack spacing={1}>
                {history.map((item, idx) => (
                  <Paper key={idx} sx={{ p: 1.5, bgcolor: '#efeae2', border: '1px solid rgba(28,29,23,0.18)' }}>
                    <Typography variant="body2"><strong>{item.event}</strong></Typography>
                    <Typography variant="caption">
                      {new Date(item.timestamp).toLocaleString()} — {item.actorRole || t('bookingDetails.system')}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Modal>

        {/* --- Модалка генерації платіжного лінку --- */}
        <Modal open={payLinkOpen} onClose={() => setPayLinkOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16', borderRadius: 2, p: { xs: 3, sm: 4 }, width: '90%', maxWidth: 400
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('bookingDetails.paymentLinkTitle')}</Typography>
              <IconButton onClick={() => setPayLinkOpen(false)} size="small" sx={{ color: '#1a1a16' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Typography sx={{ mt: 2, mb: 1 }}>{t('bookingDetails.selectServicesToPay')}:</Typography>
            <Stack spacing={1} mb={2}>
              {allServices.map(s => (
                <FormControlLabel
                  key={s.id}
                  control={
                    <Checkbox
                      checked={payCheckedServices.includes(String(s.id))}
                      onChange={() => {
                        setPayCheckedServices(prev =>
                          prev.includes(String(s.id))
                            ? prev.filter(i => i !== String(s.id))
                            : [...prev, String(s.id)]
                        );
                        setManualAmount(false);
                      }}
                      sx={{ color: '#1a1a16' }}
                    />
                  }
                  label={`${pickServiceName(s, i18n.language)} (${formatMoney(s.price)})`}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                label={t('bookingDetails.amountToPay')}
                fullWidth
                value={payAmount}
                onChange={e => {
                  setPayAmount(e.target.value);
                  setManualAmount(true);
                }}
                inputProps={{ inputMode: 'decimal' }}
              />
              <Button
                size="small"
                sx={{ color: manualAmount ? '#c9a24a' : '#57564d', minWidth: 0, px: 1 }}
                onClick={() => setManualAmount(false)}
                disabled={!manualAmount}
              >
                {t('bookingDetails.auto')}
              </Button>
            </Stack>
            <TextField
              label={t('bookingDetails.tip')}
              fullWidth
              value={payTip}
              onChange={e => setPayTip(e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ inputMode: 'decimal' }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleGeneratePaymentLink}
              sx={{
                bgcolor: '#c9a24a',
                color: '#1a1a16',
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 'none',
                mb: 2,
                '&:hover': { bgcolor: '#977536' }
              }}
            >
              {t('bookingDetails.generateLink')}
            </Button>
            {payUrl && (
              <Stack spacing={1} alignItems="center">
                <TextField
                  value={payUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={async () => {
                        await navigator.clipboard.writeText(payUrl);
                        setPayCopy(t('bookingDetails.copied'));
                        setTimeout(() => setPayCopy(''), 1500);
                      }} size="small">
                        <ContentCopyIcon sx={{ color: '#c9a24a' }} />
                      </IconButton>
                    )
                  }}
                  fullWidth
                  sx={{ bgcolor: '#efeae2', color: '#1a1a16', mb: 1 }}
                />
                {payCopy && <Alert severity="success">{payCopy}</Alert>}
              </Stack>
            )}
          </Box>
        </Modal>

        {/* --- Modal деталей по клієнту --- */}
        <Modal open={clientDetailsOpen} onClose={() => setClientDetailsOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
            boxShadow: 24, p: 3, width: '90%', maxWidth: 500, maxHeight: '85vh', overflowY: 'auto'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">{t('bookingDetails.clientDetails')}</Typography>
              <IconButton onClick={() => setClientDetailsOpen(false)} size="small" sx={{ color: '#1a1a16' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.18)', mb: 2 }} />
            {record._user && !clientEditing && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography>{t('common.name')}: <b>{record._user.name}</b></Typography>
                <Typography>{t('common.email')}: <b>{record._user.email || '—'}</b></Typography>
                <Typography>{t('common.phone')}: <b>{record._user.phone || '—'}</b></Typography>
                <Typography>{t('bookingDetails.status')}: <b>{record._user.status || '—'}</b></Typography>

                {clientSavedMsg && <Alert severity="success" sx={{ py: 0 }}>{clientSavedMsg}</Alert>}

                {/* Майстер може редагувати дані клієнта БЕЗ акаунта */}
                {record._user.hasAccount === false ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={startClientEdit}
                    sx={{ alignSelf: 'flex-start', textTransform: 'none', mt: 0.5 }}
                  >
                    {t('bookingDetails.editClient')}
                  </Button>
                ) : (
                  <Typography variant="caption" sx={{ color: '#57564d' }}>
                    {t('bookingDetails.clientHasAccountNote')}
                  </Typography>
                )}
              </Stack>
            )}

            {record._user && clientEditing && (
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label={t('common.name')}
                  value={clientForm.name}
                  onChange={e => setClientForm(f => ({ ...f, name: e.target.value }))}
                  fullWidth size="small"
                />
                <TextField
                  label={t('common.phone')}
                  value={clientForm.phone}
                  onChange={e => setClientForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+48601234567"
                  fullWidth size="small"
                />
                <TextField
                  label={t('common.email')}
                  value={clientForm.email}
                  onChange={e => setClientForm(f => ({ ...f, email: e.target.value }))}
                  fullWidth size="small"
                />
                {clientEditError && <Alert severity="error" sx={{ py: 0 }}>{clientEditError}</Alert>}
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" fullWidth onClick={() => setClientEditing(false)} disabled={clientSaving}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="contained" fullWidth onClick={handleSaveClient} disabled={clientSaving}
                    sx={{ bgcolor: '#c9a24a', color: '#1a1a16', textTransform: 'none',
                          boxShadow: 'none', '&:hover': { bgcolor: '#977536' } }}>
                    {clientSaving ? t('common.saving') : t('common.save')}
                  </Button>
                </Stack>
              </Stack>
            )}

            {/* Підсумки по клієнту */}
            {!clientLoading && clientBookings.length > 0 && (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                <Chip size="small" variant="outlined" label={`${t('bookingDetails.statTotal')}: ${clientStats.total}`} />
                <Chip size="small" color="success" label={`${t('bookingDetails.statCompleted')}: ${clientStats.completed}`} />
                {clientStats.upcoming > 0 && <Chip size="small" color="info" label={`${t('bookingDetails.statUpcoming')}: ${clientStats.upcoming}`} />}
                <Chip size="small" color="error" label={`${t('bookingDetails.statCancelled')}: ${clientStats.cancelled}`} />
                <Chip size="small" color="warning" label={`${t('bookingDetails.statNoShow')}: ${clientStats.no_show}`} />
              </Stack>
            )}

            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>{t('bookingDetails.bookingsHistory')}:</Typography>
            {clientLoading ? (
              <Typography color="secondary">{t('common.loading')}</Typography>
            ) : (
              <Stack spacing={1}>
                {clientBookings.length === 0 ? (
                  <Typography sx={{ color: '#57564d' }}>{t('bookingDetails.noBookingsFound')}</Typography>
                ) : clientBookings.map((b, idx) => {
                  const statusColor = { completed: 'success', upcoming: 'info', cancelled: 'error', no_show: 'warning' }[b.status] || 'default';
                  const statusI18n = { completed: 'statusCompleted', upcoming: 'statusUpcoming', cancelled: 'statusCancelled', no_show: 'statusNoShow' }[b.status];
                  const reasonI18n = { master: 'reasonMaster', client: 'reasonClient', no_show: 'reasonNoShow', other: 'reasonOther' }[b.reason];
                  return (
                    <Paper key={b.id ?? idx} sx={{ p: 1, bgcolor: '#faf7f2', border: '1px solid rgba(28,29,23,0.18)' }}>
                      <Typography><strong>{t('bookingDetails.date')}:</strong> {b.date} <strong>{t('bookingDetails.time')}:</strong> {b.time}</Typography>
                      <Typography><strong>{t('bookingDetails.services')}:</strong> {serviceNamesByIds(b.services) || '—'}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center" mt={1} useFlexGap flexWrap="wrap">
                        <Chip size="small" color={statusColor} label={t(`bookingDetails.${statusI18n}`)} />
                        {b.status === 'cancelled' && reasonI18n && (
                          <Typography variant="caption" sx={{ color: '#57564d' }}>
                            {t('bookingDetails.reasonLabel')}: {t(`bookingDetails.${reasonI18n}`)}
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Modal>

        {/* --- Модалка вибору причини скасування --- */}
        <Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16', borderRadius: 2, p: { xs: 3, sm: 4 }, width: '90%', maxWidth: 400
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('bookingDetails.selectCancelReason')}</Typography>
              <IconButton onClick={() => setCancelModalOpen(false)} size="small" sx={{ color: '#1a1a16' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="cancel-reason-label" sx={{ color: '#1a1a16' }}>{t('bookingDetails.reason')}</InputLabel>
              <Select
                labelId="cancel-reason-label"
                value={cancelReason}
                label={t('bookingDetails.reason')}
                onChange={e => setCancelReason(e.target.value)}
                sx={{ color: '#1a1a16', bgcolor: '#faf7f2' }}
              >
                {cancellationReasons.map(r =>
                  <MenuItem key={r.value} value={r.value}>{t(r.label)}</MenuItem>
                )}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button variant="contained" fullWidth onClick={handleCancelBooking} disabled={!cancelReason}>
                {t('bookingDetails.confirmCancellation')}
              </Button>
              <Button variant="outlined" fullWidth onClick={() => setCancelModalOpen(false)}>
                {t('common.cancel')}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* --- Підтвердження переносу запису в минуле --- */}
        <Modal open={pastConfirmOpen} onClose={() => setPastConfirmOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
            boxShadow: 24, p: { xs: 3, sm: 4 }, width: '90%', maxWidth: 420
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              ⚠️ {t('bookingDetails.pastConfirmTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#57564d', mb: 1 }}>
              {t('bookingDetails.pastConfirmText', {
                date: editedDate ? editedDate.format('YYYY-MM-DD') : '',
                time: editedTime,
              })}
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button variant="outlined" fullWidth onClick={() => setPastConfirmOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleSaveChanges(true)}
                disabled={saving}
                sx={{ bgcolor: '#977536', color: '#fff', fontWeight: 600, textTransform: 'none',
                      boxShadow: 'none', '&:hover': { bgcolor: '#7d611f' } }}
              >
                {saving ? t('common.saving') : t('bookingDetails.pastConfirmYes')}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* --- Зміна ціни запису (будь-коли, без сповіщення клієнту) --- */}
        <Modal open={priceEditOpen} onClose={() => setPriceEditOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
            boxShadow: 24, p: { xs: 3, sm: 4 }, width: '90%', maxWidth: 400
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">{t('bookingDetails.changePriceTitle')}</Typography>
              <IconButton onClick={() => setPriceEditOpen(false)} size="small" sx={{ color: '#1a1a16' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ borderColor: 'rgba(28,29,23,0.18)', mb: 2 }} />
            <TextField
              fullWidth
              label={t('bookingDetails.priceField')}
              value={newPrice}
              onChange={e => setNewPrice(e.target.value)}
              placeholder={String(servicesSum)}
              helperText={t('bookingDetails.priceHint')}
              inputProps={{ inputMode: 'decimal' }}
              autoFocus
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" sx={{ color: '#57564d', display: 'block', mb: 2 }}>
              {t('bookingDetails.changePriceNoNotify')}
            </Typography>
            {priceError && <Alert severity="error" sx={{ mb: 2 }}>{priceError}</Alert>}
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" fullWidth onClick={() => setPriceEditOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSavePrice}
                disabled={priceSaving}
                sx={{ bgcolor: '#c9a24a', color: '#1a1a16', fontWeight: 600, textTransform: 'none',
                      boxShadow: 'none', '&:hover': { bgcolor: '#977536' } }}
              >
                {priceSaving ? t('common.saving') : t('common.save')}
              </Button>
            </Stack>
          </Box>
        </Modal>

        {/* --- Повідомити клієнта: SMS/email, шаблони 3 мовами --- */}
        <Modal open={notifyOpen} onClose={() => setNotifyOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#faf7f2', color: '#1a1a16',
            border: '1px solid rgba(28,29,23,0.12)', borderRadius: 2,
            boxShadow: 24, p: { xs: 2.5, sm: 3 }, width: '92%', maxWidth: 520,
            maxHeight: '90vh', overflowY: 'auto'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">✉️ {t('notify.title')}</Typography>
              <IconButton onClick={() => setNotifyOpen(false)} size="small" sx={{ color: '#1a1a16' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Typography variant="body2" sx={{ color: '#57564d', mb: 2 }}>
              {t('bookingDetails.client')}: <b>{record.clientName}</b>
              {record.clientPhone ? ` · ${record.clientPhone}` : ''}
              {record.clientEmail ? ` · ${record.clientEmail}` : ''}
            </Typography>

            {/* Мова + шаблон */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField select label={t('notify.language')} value={notifyLang}
                onChange={(e) => changeLang(e.target.value)} fullWidth size="small">
                {MESSAGE_LANGS.map((l) => (
                  <MenuItem key={l} value={l}>{t(`notify.lang_${l}`)}</MenuItem>
                ))}
              </TextField>
              <TextField select label={t('notify.template')} value={notifyTemplate}
                onChange={(e) => changeTemplate(e.target.value)} fullWidth size="small">
                {MESSAGE_TEMPLATES.map((tpl) => (
                  <MenuItem key={tpl.key} value={tpl.key}>{t(`notify.tpl_${tpl.key}`)}</MenuItem>
                ))}
              </TextField>
            </Stack>

            {/* Код дверей — лише для шаблонів, що його вживають */}
            {MESSAGE_TEMPLATES.find((x) => x.key === notifyTemplate)?.needsCode && (
              <TextField
                label={t('notify.doorCode')}
                value={notifyCode}
                onChange={(e) => changeCode(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                helperText={t('notify.doorCodeHint')}
              />
            )}

            {/* Канали */}
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Checkbox checked={notifySms} disabled={!record.clientPhone}
                  onChange={(e) => setNotifySms(e.target.checked)} sx={{ color: '#1a1a16' }} />}
                label={record.clientPhone ? 'SMS' : t('notify.noPhone')}
              />
              <FormControlLabel
                control={<Checkbox checked={notifyEmail} disabled={!record.clientEmail}
                  onChange={(e) => setNotifyEmail(e.target.checked)} sx={{ color: '#1a1a16' }} />}
                label={record.clientEmail ? 'Email' : t('notify.noEmail')}
              />
            </Stack>

            {/* Тема (для email) */}
            {notifyEmail && (
              <TextField
                label={t('notify.subject')}
                value={notifySubject}
                onChange={(e) => setNotifySubject(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />
            )}

            {/* Текст повідомлення (редагований) */}
            <TextField
              label={t('notify.message')}
              value={notifyBody}
              onChange={(e) => setNotifyBody(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              sx={{ mb: 1 }}
            />
            <Typography variant="caption" sx={{ color: '#8a887f', display: 'block', mb: 2 }}>
              {notifyBody.length} {t('notify.chars')}
            </Typography>

            {notifyError && <Alert severity="error" sx={{ mb: 2 }}>{notifyError}</Alert>}
            {notifyResult?.ok && <Alert severity="success" sx={{ mb: 2 }}>{notifyResult.msg}</Alert>}

            <Stack direction="row" spacing={2}>
              <Button variant="outlined" fullWidth onClick={() => setNotifyOpen(false)}>
                {t('common.close')}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleSendNotify}
                disabled={notifySending || (!notifySms && !notifyEmail)}
                sx={{ bgcolor: '#5e6a4e', color: '#fff', textTransform: 'none',
                      boxShadow: 'none', '&:hover': { bgcolor: '#4a5540' } }}
              >
                {notifySending ? t('notify.sending') : t('notify.send')}
              </Button>
            </Stack>
          </Box>
        </Modal>

      </Container>
    </LocalizationProvider>
  );
}
